import { Component, MeshComponent, Property } from '@wonderlandengine/api';
import { vec3 } from 'gl-matrix';

/**
 * kinematic-bullet
 *
 * Physics-free pooled bullet. Movement is handled manually every frame
 * via translateWorld(), so no PhysXComponent is needed on the object.
 *
 * Lifecycle:
 *   arena-handler calls fire(linVel) to activate a bullet from the pool.
 *   update(dt) translates it each frame and counts down its TTL.
 *   deactivateBullet() hides it and marks it inactive so the pool can reuse it.
 *
 * The object must have a MeshComponent child (or on itself) for visibility.
 * There is NO destroy() call — objects are always pooled and recycled.
 */
export class KinematicBullet extends Component {
    static TypeName = 'kinematic-bullet';
    static Properties = {
        maxLifetime: Property.float(2.0),  // seconds before auto-deactivation
        bulletSpeedScale: Property.float(0.5),
    };

    init() {
        // Pooling state — readable by arena-handler
        this.shooterId = null;

        // Internal tracking
        this._isActive = false;
        this._lifeTimer = 0;
        this.originalScaling = vec3.fromValues(0.025, 0.025, 0.420); // Default bullet scaling (can be overridden in editor)
        this.scaleUpVector = vec3.create();
        // Pre-allocated velocity vector (world-space units per second)
        // Written by fire(), read every frame in update()
        this._velocity = new Float32Array(3);
        this._currentPos = vec3.create();
        this._targetPos = vec3.create();
        this._tempDir = vec3.create(); // For calculating spawn offset without GC
        // Pre-allocated translation scratch buffer (avoids GC every frame)
        this._delta = new Float32Array(3);

        // Where to hide the bullet when inactive (far below the scene)
        this._hiddenPos = new Float32Array([0, -1000, 0]);
    }

    start() {
        this.object.setScalingWorld(this.originalScaling); // ensure correct scaling in case the prefab was modified
        // Register session-end handlers so bullets reset cleanly between sessions
        if (this.engine?.onXRSessionEnd) {
            this._boundXREnd = this._resetSession.bind(this);
            this.engine.onXRSessionEnd.add(this._boundXREnd);
        }
        this._boundDesktopEnd = this._resetSession.bind(this);
        window.addEventListener('desktop-session-end', this._boundDesktopEnd);
        // Start hidden
        this.object.setPositionWorld(this._hiddenPos);
        this.active = false;
    }

    onDestroy() {
        if (this._boundXREnd && this.engine?.onXRSessionEnd) {
            this.engine.onXRSessionEnd.remove(this._boundXREnd);
        }
        if (this._boundDesktopEnd) {
            window.removeEventListener('desktop-session-end', this._boundDesktopEnd);
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Public API called by arena-handler
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Activate this bullet from the pool.
     *
     * arena-handler already calls bullet.setPositionWorld(pos) BEFORE calling
     * fire(), so we do not need a position parameter — we just store the velocity
     * and start the timer.
     *
     * @param {ArrayLike<number>} linVel  World-space velocity in units/second.
     *                                    Matches the existing linVel convention:
     *                                    the vector already encodes both direction
     *                                    and speed (e.g. normalized dir × 1000).
     */
    fire(linVel) {
        // 1. Calculate and store final velocity
        vec3.scale(this._velocity, linVel, this.bulletSpeedScale);

        // 2. Reset lifetime counter   
        this._lifeTimer = 0;

        // 3. Get initial position
        this.object.getPositionWorld(this._currentPos);

        // 4. Calculate target pos for lookAt
        vec3.add(this._targetPos, this._currentPos, this._velocity);
        this.object.lookAt(this._targetPos, [0, 1, 0]);

        // 6. Mark active for both the pool system and this component's update()
        this._isActive = true;
        this.active = true;
    }

    /**
     * Deactivate and return to pool.
     * Safe to call multiple times — guarded by _isActive.
     */
    deactivateBullet() {
        if (!this._isActive) return;
        this.object.setScalingWorld(this.originalScaling);
        this._isActive = false;
        this.active = false;
        this.shooterId = null;

        this._velocity[0] = 0;
        this._velocity[1] = 0;
        this._velocity[2] = 0;
        this._lifeTimer = 0;
        this.object.setPositionWorld(this._hiddenPos);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Per-frame movement
    // ─────────────────────────────────────────────────────────────────────────

    update(dt) {
        // CRITICAL optimisation: early-exit for every inactive bullet in the pool.
        // With 50-100 pooled bullets this saves ~95% of bullet update CPU.
        if (!this._isActive) return;

        // Advance lifetime and deactivate when TTL is reached
        this._lifeTimer += dt;
        if (this._lifeTimer >= this.maxLifetime) {
            this.deactivateBullet();
            return;
        }
        // 1. Calculate movement delta
        vec3.scale(this._delta, this._velocity, dt);
        // 3. Apply translation
        this.object.translateWorld(this._delta);
    }
    // ─────────────────────────────────────────────────────────────────────────
    // Session teardown
    // ─────────────────────────────────────────────────────────────────────────

    _resetSession() {
        this._isActive = false;
        this.active = false;
        this.shooterId = null;
        this._velocity[0] = 0;
        this._velocity[1] = 0;
        this._velocity[2] = 0;
        this._lifeTimer = 0;
        this.object.setPositionWorld(this._hiddenPos);
    }
}